import { createSlice } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '../store';
import type { TripLegRequestV2 } from '../requests/trip-legs';
import { TripLegDiscussionDecision } from '../models/trip-leg-discussion-decision.model';
import {
  TripLegStatus,
  TripLegStatusBC,
} from '../models/trip-leg-status.model';
import { RejectTripLegRequest } from '../models/trip-leg-reject.model';

import triplegService from '../../services/triplegService';
import reservationService from '../../services/reservationService';
import * as uiSlice from '../ui.slice';
import { removeTripManagementDiscussTrip } from './trip-leg.slice';

interface DiscussionState {
  tripLegDiscussion: TripLegRequestV2 | null;
  loadingTripLegDiscussion: boolean;
  loadedTripLegDiscussion: boolean;
  clearingDiscussion: boolean;
  clearedDiscussion: boolean;
  pendingDiscussionClear: {
    tripLegID: number;
    tripLegReferenceID: string;
    decision: TripLegDiscussionDecision;
    tripLegExceptionID?: number;
  } | null;
}

const initialState: DiscussionState = {
  tripLegDiscussion: null,
  loadingTripLegDiscussion: false,
  loadedTripLegDiscussion: false,
  clearingDiscussion: false,
  clearedDiscussion: false,
  pendingDiscussionClear: null,
};

const discussionSlice = createSlice({
  name: 'discussion',
  initialState,
  reducers: {
    setLoadingTripLegDiscussion: (
      state: DiscussionState,
      action: { payload: boolean },
    ) => {
      state.loadingTripLegDiscussion = action.payload;
    },
    setLoadedTripLegDiscussion: (
      state: DiscussionState,
      action: { payload: boolean },
    ) => {
      state.loadedTripLegDiscussion = action.payload;
    },
    setTripLegDiscussion: (
      state: DiscussionState,
      action: { payload: TripLegRequestV2 | null },
    ) => {
      state.tripLegDiscussion = action.payload;
    },
    setClearingDiscussion: (
      state: DiscussionState,
      action: { payload: boolean },
    ) => {
      state.clearingDiscussion = action.payload;
    },
    setClearedDiscussion: (
      state: DiscussionState,
      action: { payload: boolean },
    ) => {
      state.clearedDiscussion = action.payload;
    },
    setPendingDiscussionClear: (
      state: DiscussionState,
      action: {
        payload: {
          tripLegID: number;
          tripLegReferenceID: string;
          decision: TripLegDiscussionDecision;
          tripLegExceptionID?: number;
        } | null;
      },
    ) => {
      state.pendingDiscussionClear = action.payload;
    },
  },
});

const {
  setLoadingTripLegDiscussion,
  setLoadedTripLegDiscussion,
  setTripLegDiscussion,
  setClearingDiscussion,
  setClearedDiscussion,
  setPendingDiscussionClear,
} = discussionSlice.actions;

const getDiscussionAsync = (tripLegID: number) => (dispatch: AppDispatch) => {
  dispatch(setTripLegDiscussion(null));
  dispatch(setLoadingTripLegDiscussion(true));
  dispatch(setLoadedTripLegDiscussion(false));
  dispatch(setClearingDiscussion(false));
  dispatch(setClearedDiscussion(false));
  triplegService.getDiscussion({ tripLegID }).subscribe({
    next: (discussion) => {
      dispatch(setTripLegDiscussion(discussion));
      dispatch(setLoadedTripLegDiscussion(true));
    },
    error: () => {
      dispatch(setLoadingTripLegDiscussion(false));
      dispatch(setLoadedTripLegDiscussion(false));
    },
    complete: () => {
      dispatch(setLoadingTripLegDiscussion(false));
    },
  });
};

const clearUnderDiscussion =
  (
    tripLegID: number,
    tripLegReferenceID: string,
    decision: TripLegDiscussionDecision,
    tripLegExceptionID?: number,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setClearingDiscussion(true));
    dispatch(setClearedDiscussion(false));
    triplegService.clearUnderDiscussion({ tripLegID, decision }).subscribe({
      next: () => {
        if (tripLegExceptionID) {
          triplegService
            .setException({ tripLegID, tripLegExceptionID })
            .subscribe({
              next: () => {
                dispatch(setClearedDiscussion(true));
                dispatch(
                  uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
                );
                dispatch(removeTripManagementDiscussTrip(tripLegReferenceID));
              },
              error: () => {
                dispatch(setClearedDiscussion(false));
                dispatch(setClearingDiscussion(false));
                dispatch(
                  uiSlice.setErrorMessage('snackbar.clearDiscussionFailure'),
                );
              },
              complete: () => {
                dispatch(setClearingDiscussion(false));
              },
            });
        } else {
          dispatch(setClearedDiscussion(true));
          dispatch(
            uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
          );
          dispatch(removeTripManagementDiscussTrip(tripLegReferenceID));
        }
      },
      error: () => {
        dispatch(setClearedDiscussion(false));
        dispatch(setClearingDiscussion(false));
        dispatch(uiSlice.setErrorMessage('snackbar.clearDiscussionFailure'));
      },
      complete: () => {
        if (!tripLegExceptionID) {
          dispatch(setClearingDiscussion(false));
        }
      },
    });
  };

type TripLegStatusUpdate = {
  passengerUUID: string;
  tripLegReferenceID: string;
  action: TripLegStatus;
  tripLegStatusReasonBCs?: string[];
  onSiteDateTimeUTC?: string;
  onBoardDateTimeUTC?: string;
  completedDateTimeUTC?: string;
  noLoadDateTimeUTC?: string;
  cancellationDateTimeUTC?: string;
};

type DriverAssignment = {
  tripLegReferenceID: string;
  driverID: number;
};

const submitAdjustTrip =
  (
    tripLegID: number,
    tripLegReferenceID: string,
    decision: TripLegDiscussionDecision,
    previousStatus?: string,
    tripLegStatusUpdate?: TripLegStatusUpdate,
    driverAssignment?: DriverAssignment,
    rejectTripLegInfo?: RejectTripLegRequest,
    tripLegExceptionID?: number,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setClearingDiscussion(true));
    dispatch(setClearedDiscussion(false));

    const handleError = (msg?: string) => {
      dispatch(setClearedDiscussion(false));
      dispatch(uiSlice.setErrorMessage('snackbar.clearDiscussionFailure', msg));
      dispatch(setClearingDiscussion(false));
    };

    const finishClearDiscussion = () => {
      triplegService.clearUnderDiscussion({ tripLegID, decision }).subscribe({
        next: () => {
          if (tripLegExceptionID) {
            triplegService
              .setException({ tripLegID, tripLegExceptionID })
              .subscribe({
                next: () => {
                  dispatch(setClearedDiscussion(true));
                  dispatch(
                    uiSlice.setSuccessMessage(
                      'snackbar.clearDiscussionSuccess',
                    ),
                  );
                  dispatch(removeTripManagementDiscussTrip(tripLegReferenceID));
                },
                error: (err) => handleError(err.message),
                complete: () => {
                  dispatch(setClearingDiscussion(false));
                },
              });
          } else {
            dispatch(setClearedDiscussion(true));
            dispatch(
              uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
            );
            dispatch(removeTripManagementDiscussTrip(tripLegReferenceID));
          }
        },
        error: (err) => {
          dispatch(setClearedDiscussion(false));
          dispatch(
            uiSlice.setErrorMessage(
              'snackbar.clearDiscussionFailure',
              err.message,
            ),
          );
        },
        complete: () => {
          if (!tripLegExceptionID) {
            dispatch(setClearingDiscussion(false));
          }
        },
      });
    };

    if (decision.discussionDecisionOutcome === 'NOTSERVICED') {
      const rejectPayload =
        rejectTripLegInfo ||
        (tripLegStatusUpdate && tripLegStatusUpdate.tripLegStatusReasonBCs
          ? {
              passengerUUID: tripLegStatusUpdate.passengerUUID,
              tripLegReferenceID: tripLegStatusUpdate.tripLegReferenceID,
              tripLegRejectedReasonBC:
                tripLegStatusUpdate.tripLegStatusReasonBCs[0],
            }
          : undefined);

      if (!rejectPayload) {
        handleError('Missing rejection details');
        return;
      }

      return reservationService.rejectTripLeg(rejectPayload).subscribe({
        next: () => {
          finishClearDiscussion();
        },
        error: (err) => {
          dispatch(setClearedDiscussion(false));
          dispatch(
            uiSlice.setErrorMessage(
              'snackbar.clearDiscussionFailure',
              err.message,
            ),
          );
          dispatch(setClearingDiscussion(false));
        },
      });
    }

    if (!tripLegStatusUpdate) {
      return finishClearDiscussion();
    }

    if (
      previousStatus === TripLegStatus.Completed &&
      tripLegStatusUpdate.action === TripLegStatus.NoLoad
    ) {
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.Onsite,
          tripLegStatusUpdate.onSiteDateTimeUTC!,
        )
        .subscribe({
          next: () => {
            return reservationService
              .updateTripLegLog(
                TripLegStatusBC.NoLoad,
                tripLegStatusUpdate.passengerUUID,
                tripLegStatusUpdate.tripLegReferenceID,
                tripLegStatusUpdate.noLoadDateTimeUTC!,
              )
              .subscribe({
                next: () => {
                  if (driverAssignment) {
                    triplegService
                      .assignDriver({
                        tripLegReferenceID: driverAssignment.tripLegReferenceID,
                        driverID: driverAssignment.driverID,
                        sendSMS: false,
                      })
                      .subscribe({
                        next: () => finishClearDiscussion(),
                        error: (err) => handleError(err.message),
                      });
                  } else {
                    finishClearDiscussion();
                  }
                },
                error: (err) => handleError(err.message),
              });
          },
          error: (err) => handleError(err.message),
        });
    }

    if (tripLegStatusUpdate.action === TripLegStatus.NoLoad) {
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.NoLoad,
          tripLegStatusUpdate.noLoadDateTimeUTC!,
        )
        .subscribe({
          next: () => {
            return reservationService
              .updateTripLegLog(
                TripLegStatusBC.Onsite,
                tripLegStatusUpdate.passengerUUID,
                tripLegStatusUpdate.tripLegReferenceID,
                tripLegStatusUpdate.onSiteDateTimeUTC!,
              )
              .subscribe({
                next: () => {
                  if (driverAssignment) {
                    triplegService
                      .assignDriver({
                        tripLegReferenceID: driverAssignment.tripLegReferenceID,
                        driverID: driverAssignment.driverID,
                        sendSMS: false,
                      })
                      .subscribe({
                        next: () => finishClearDiscussion(),
                        error: (err) => handleError(err.message),
                      });
                  } else {
                    finishClearDiscussion();
                  }
                },
                error: (err) => handleError(err.message),
              });
          },
          error: (err) => handleError(err.message),
        });
    }

    if (tripLegStatusUpdate.action === TripLegStatus.CanceledLate) {
      // Generic -> CanceledLate: override with reason code, then wait for the
      // TripLegCanceledLate WS event before calling clearUnderDiscussion.
      // The backend emits the event only after fully committing the status
      // change, so the driver-required validation in clearUnderDiscussion is
      // safe to call only after that event arrives.
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.CanceledLate,
          tripLegStatusUpdate.cancellationDateTimeUTC!,
          [TripLegStatusBC.LateDoctorReschedule],
        )
        .subscribe({
          next: () => {
            dispatch(
              setPendingDiscussionClear({
                tripLegID,
                tripLegReferenceID: tripLegStatusUpdate.tripLegReferenceID,
                decision,
                tripLegExceptionID,
              }),
            );
          },
          error: (err) => handleError(err.message),
        });
    }

    if (tripLegStatusUpdate.action === TripLegStatus.Canceled) {
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.Canceled,
          tripLegStatusUpdate.cancellationDateTimeUTC!,
          [TripLegStatusBC.LateDoctorReschedule],
        )
        .subscribe({
          next: () => {
            finishClearDiscussion();
          },
          error: (err) => handleError(err.message),
        });
    }

    if (tripLegStatusUpdate.action === TripLegStatus.Completed) {
      return reservationService
        .overrideTripLegStatus(
          tripLegStatusUpdate.passengerUUID,
          tripLegStatusUpdate.tripLegReferenceID,
          TripLegStatus.Completed,
          tripLegStatusUpdate.completedDateTimeUTC!,
        )
        .subscribe({
          next: () => {
            return reservationService
              .updateTripLegLog(
                TripLegStatusBC.Onsite,
                tripLegStatusUpdate.passengerUUID,
                tripLegStatusUpdate.tripLegReferenceID,
                tripLegStatusUpdate.onSiteDateTimeUTC!,
              )
              .subscribe({
                next: () => {
                  reservationService
                    .updateTripLegLog(
                      TripLegStatusBC.Onboard,
                      tripLegStatusUpdate.passengerUUID,
                      tripLegStatusUpdate.tripLegReferenceID,
                      tripLegStatusUpdate.onBoardDateTimeUTC!,
                    )
                    .subscribe({
                      next: () => {
                        if (driverAssignment) {
                          triplegService
                            .assignDriver({
                              tripLegReferenceID:
                                driverAssignment.tripLegReferenceID,
                              driverID: driverAssignment.driverID,
                              sendSMS: false,
                            })
                            .subscribe({
                              next: () => finishClearDiscussion(),
                              error: (err) => handleError(err.message),
                            });
                        } else {
                          finishClearDiscussion();
                        }
                      },
                      error: (err) => handleError(err.message),
                    });
                },
                error: (err) => handleError(err.message),
              });
          },
          error: (err) => handleError(err.message),
        });
    }
    // TODO: there may be more scenarios to cover.
  };

const completePendingDiscussionClearAsync =
  (tripLegReferenceID: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const pending = getState().discussion.pendingDiscussionClear;
    if (!pending || pending.tripLegReferenceID !== tripLegReferenceID) {
      return;
    }

    dispatch(setPendingDiscussionClear(null));

    const { tripLegID, decision, tripLegExceptionID } = pending;

    triplegService.clearUnderDiscussion({ tripLegID, decision }).subscribe({
      next: () => {
        if (tripLegExceptionID) {
          triplegService
            .setException({ tripLegID, tripLegExceptionID })
            .subscribe({
              next: () => {
                dispatch(setClearedDiscussion(true));
                dispatch(
                  uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
                );
                dispatch(removeTripManagementDiscussTrip(tripLegReferenceID));
              },
              error: () => {
                dispatch(setClearedDiscussion(false));
                dispatch(setClearingDiscussion(false));
                dispatch(
                  uiSlice.setErrorMessage('snackbar.clearDiscussionFailure'),
                );
              },
              complete: () => {
                dispatch(setClearingDiscussion(false));
              },
            });
        } else {
          dispatch(setClearedDiscussion(true));
          dispatch(
            uiSlice.setSuccessMessage('snackbar.clearDiscussionSuccess'),
          );
          dispatch(removeTripManagementDiscussTrip(tripLegReferenceID));
        }
      },
      error: () => {
        dispatch(setClearedDiscussion(false));
        dispatch(setClearingDiscussion(false));
        dispatch(uiSlice.setErrorMessage('snackbar.clearDiscussionFailure'));
      },
      complete: () => {
        if (!tripLegExceptionID) {
          dispatch(setClearingDiscussion(false));
        }
      },
    });
  };

const selectTripLegDiscussion = (state: RootState) =>
  state.discussion.tripLegDiscussion;
const selectLoadingTripLegDiscussion = (state: RootState) =>
  state.discussion.loadingTripLegDiscussion;
const selectLoadedTripLegDiscussion = (state: RootState) =>
  state.discussion.loadedTripLegDiscussion;
const selectClearingDiscussion = (state: RootState) =>
  state.discussion.clearingDiscussion;
const selectClearedDiscussion = (state: RootState) =>
  state.discussion.clearedDiscussion;

export type { TripLegStatusUpdate, DriverAssignment };
export {
  discussionSlice,
  getDiscussionAsync,
  clearUnderDiscussion,
  submitAdjustTrip,
  completePendingDiscussionClearAsync,
  selectTripLegDiscussion,
  selectLoadingTripLegDiscussion,
  selectLoadedTripLegDiscussion,
  selectClearingDiscussion,
  selectClearedDiscussion,
};
